// ============================================================
// RosterContext - the unified combatant roster and the two local
// selection pointers (who I'm acting as, who I'm targeting).
//
// High update frequency (every wound, condition, or selection change) -
// keep consumers below the provider memoized.
//
// State itself stays in DeathWatchSimulator's hooks; this context
// only distributes it (composition-root pattern).
//
// During phase B, TargetsContext still exists and is fed from this same
// roster through the legacy adapter, so components see no change. Phase
// C retires it.
// ============================================================
import { createContext, useContext } from 'react';

const RosterContext = createContext(null);

export function RosterProvider({ value, children }) {
  return <RosterContext.Provider value={value}>{children}</RosterContext.Provider>;
}

/**
 * Roster state + handlers:
 *   roster, setRoster,
 *   actorId, setActorId, actor,      - who this browser is acting as (local, not synced)
 *   targetId, setTargetId, target,   - who this browser is targeting (local, not synced)
 *   updateCombatantState,            - wounds/conditions/crits/ammo (undoable, synced hot)
 *   updateCombatantSheet,            - characteristics/talents/weapons (NOT undoable)
 *   appendNarrative
 */
/**
 * @returns {any} the roster bucket.
 *
 * Untyped on purpose, and annotated so it stays usable from TypeScript:
 * `createContext(null)` plus a throw-guard narrows the return to `never`,
 * so a .tsx consumer gets "Property 'x' does not exist on type 'never'".
 */
export function useRosterContext() {
  const ctx = useContext(RosterContext);
  if (!ctx) throw new Error('useRosterContext must be used within a RosterProvider');
  return ctx;
}

export default RosterContext;
